// Role card with flip animation.
//
// The OUTER shell (240×360 rounded rect, perspective flip, shared back face)
// stays the same for every role. The FRONT face is dispatched to one of six
// "archetype" templates so roles in different categories look like they came
// from different decks — see ./cardArchetype.ts.
//
// The BACK face is GENERIC across every role on purpose: a face-down card
// must not leak which role it is, otherwise the reveal moment is dead on
// arrival. Archetype only changes the front.

import type { CSSProperties, ReactNode } from 'react';
import { RoleArt } from './RoleArt.js';
import { archetypeOf, type CardArchetype } from './cardArchetype.js';
import type { Lang, RoleConfig } from '../../../shared/src/types.js';

interface Props {
  role: RoleConfig;
  t: Lang;
  flipped: boolean;
  onFlip?: () => void;
  /** When true, the front face plays its archetype-specific entrance
   *  animation. Owner toggles this back to false once the animation has
   *  played (~1100ms is enough for any archetype). MUST only be set after
   *  the rotateY transition has landed (front face is fully visible),
   *  otherwise the entrance plays on the back side of the card and shows
   *  through as mirrored content during the second half of the flip. */
  revealing?: boolean;
  /** When true, fires the light-burst overlay outside the rc-inner. This is
   *  what punctuates the actual flip moment. Independent from `revealing`
   *  so the burst can fire while the card is mid-rotation while the
   *  entrance waits for the flip to land. */
  flashing?: boolean;
}

const FACTION_LABEL: Record<string, Record<Lang, string>> = {
  town: { ru: 'МИРНЫЕ', uk: 'МИРНІ' },
  mafia: { ru: 'МАФИЯ', uk: 'МАФІЯ' },
  solo: { ru: 'ОДИНОЧКА', uk: 'ОДИНАК' },
};

export function RoleCard({ role, t, flipped, onFlip, revealing, flashing }: Props) {
  const arch = archetypeOf(role.id);
  const factionLabel = FACTION_LABEL[role.faction][t];

  return (
    <div className={`role-card rc-arch-${arch}`} data-flipped={flipped} onClick={onFlip}>
      {/* Sits OUTSIDE .rc-inner so it doesn't flip with the card; this is the
          light burst that goes off the moment the card flips. Driven by
          `flashing`, which fires DURING the rotateY transition. */}
      <div className="rc-flash" data-revealing={flashing ? 'true' : undefined} aria-hidden />
      <div className="rc-inner">
        <div
          className={`rc-face rc-front rc-${role.faction} rc-${arch}`}
          data-revealing={revealing ? 'true' : undefined}
        >
          {renderArchetype(arch, role, t, factionLabel)}
        </div>
        <DeckBack />
      </div>
    </div>
  );
}

// Generic deck back — IDENTICAL on every card. No role accent, no role
// title, no faction, nothing the front would reveal. This is what makes a
// face-down card actually face-down.
function DeckBack() {
  return (
    <div className="rc-face rc-back">
      <div className="rc-back-velvet" />
      <div className="rc-back-damask" />
      <div className="rc-back-frame-outer" />
      <div className="rc-back-frame-inner" />
      <span className="rc-back-corner rc-back-corner-tl" aria-hidden />
      <span className="rc-back-corner rc-back-corner-tr" aria-hidden />
      <span className="rc-back-corner rc-back-corner-bl" aria-hidden />
      <span className="rc-back-corner rc-back-corner-br" aria-hidden />
      <div className="rc-back-wordmark mono rc-back-wordmark-top">MAFIA</div>
      <div className="rc-back-medallion" aria-hidden>
        <svg viewBox="0 0 100 100" width="100%" height="100%">
          <defs>
            <radialGradient id="rc-back-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(255,42,60,0.55)" />
              <stop offset="55%" stopColor="rgba(120,12,18,0.35)" />
              <stop offset="100%" stopColor="rgba(20,4,8,0)" />
            </radialGradient>
          </defs>
          <circle cx="50" cy="50" r="42" fill="url(#rc-back-glow)" />
          <circle cx="50" cy="50" r="34" fill="none" stroke="#7a1820" strokeWidth="1.4" />
          <circle cx="50" cy="50" r="26" fill="none" stroke="#7a1820" strokeWidth="0.8" opacity="0.6" />
          {/* stylised "M" — heavy serif, slight droop */}
          <path
            d="M30 68 L30 32 L40 32 L50 52 L60 32 L70 32 L70 68 L62 68 L62 44 L52 60 L48 60 L38 44 L38 68 Z"
            fill="#f0e6d4"
            opacity="0.92"
          />
          {/* crossed daggers behind the M — subtle silhouettes */}
          <path d="M22 24 L78 76" stroke="#7a1820" strokeWidth="1.2" opacity="0.4" />
          <path d="M78 24 L22 76" stroke="#7a1820" strokeWidth="1.2" opacity="0.4" />
        </svg>
      </div>
      <div className="rc-back-wordmark mono rc-back-wordmark-bot">NOIR</div>
      <div className="rc-back-pip rc-back-pip-tl mono">M</div>
      <div className="rc-back-pip rc-back-pip-br mono">M</div>
    </div>
  );
}

function renderArchetype(arch: CardArchetype, role: RoleConfig, t: Lang, factionLabel: string): ReactNode {
  switch (arch) {
    case 'dossier':  return <DossierFront role={role} t={t} factionLabel={factionLabel} />;
    case 'wanted':   return <WantedFront role={role} t={t} factionLabel={factionLabel} />;
    case 'tarot':    return <TarotFront role={role} t={t} factionLabel={factionLabel} />;
    case 'parlor':   return <ParlorFront role={role} t={t} factionLabel={factionLabel} />;
    case 'infernal': return <InfernalFront role={role} t={t} factionLabel={factionLabel} />;
    case 'vellum':
    default:         return <VellumFront role={role} t={t} factionLabel={factionLabel} />;
  }
}

interface FrontProps {
  role: RoleConfig;
  t: Lang;
  factionLabel: string;
}

// ──────────────────────────────────────────────────────────────────────────
// DOSSIER — manila case file with tab, eyelet, redaction bars, classified
// stamp.
// ──────────────────────────────────────────────────────────────────────────
function DossierFront({ role, t, factionLabel }: FrontProps) {
  const a = role.accent;
  return (
    <div className="dos">
      <div className="dos-paper" />
      <div className="dos-fibers" />
      {/* manila tab — sticks up from the top-right edge of the folder */}
      <div className="dos-tab">
        <span className="mono">№{role.number}</span>
      </div>
      {/* dirty red banner */}
      <div className="dos-banner mono">
        <span>CASE FILE</span>
        <span className="dos-banner-dot" />
        <span>{factionLabel}</span>
      </div>
      {/* paper hole + reinforcement ring */}
      <div className="dos-hole" />
      {/* center "photo" with paperclip and corner stamp */}
      <div className="dos-photo" style={{ borderColor: a }}>
        <div className="dos-photo-art">
          <RoleArt id={role.id} accent={a} sub={role.sub} />
        </div>
        <div className="dos-paperclip" />
        <div className="dos-corner-stamp mono" style={{ color: a, borderColor: a }}>
          {role.latin.slice(0, 3)}
        </div>
      </div>
      {/* typewritten label with redaction bars */}
      <div className="dos-label">
        <div className="dos-redact-row">
          <span className="dos-redact" />
          <span className="dos-redact dos-redact-short" />
        </div>
        <div className="dos-name mono">{role.title[t].toUpperCase()}</div>
        <div className="dos-meta mono">
          <span>{t === 'ru' ? 'СУБЪЕКТ' : 'СУБ’ЄКТ'} · {role.latin}</span>
        </div>
      </div>
      {/* CLASSIFIED diagonal stamp in red */}
      <div className="dos-classified" aria-hidden>
        <span className="display">{t === 'ru' ? 'СЕКРЕТНО' : 'ТАЄМНО'}</span>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// WANTED — torn bounty poster, large stencil headline, reward strip.
// ──────────────────────────────────────────────────────────────────────────
function WantedFront({ role, t }: FrontProps) {
  const a = role.accent;
  const reward = rewardFor(role.id);
  return (
    <div className="wnt">
      <div className="wnt-paper" />
      <div className="wnt-stains" />
      {/* torn top edge */}
      <svg className="wnt-tear wnt-tear-top" viewBox="0 0 240 14" preserveAspectRatio="none" aria-hidden>
        <path d="M0 0 L240 0 L240 8 L226 4 L210 11 L196 6 L180 12 L164 5 L148 10 L132 4 L116 11 L100 6 L84 12 L68 5 L52 10 L36 4 L20 11 L0 6 Z" fill="#0a0a0c" />
      </svg>
      <svg className="wnt-tear wnt-tear-bot" viewBox="0 0 240 14" preserveAspectRatio="none" aria-hidden>
        <path d="M0 14 L240 14 L240 6 L226 10 L210 3 L196 8 L180 2 L164 9 L148 4 L132 10 L116 3 L100 8 L84 2 L68 9 L52 4 L36 10 L20 3 L0 8 Z" fill="#0a0a0c" />
      </svg>
      {/* nail holes in corners */}
      <span className="wnt-nail wnt-nail-tl" />
      <span className="wnt-nail wnt-nail-tr" />
      <span className="wnt-nail wnt-nail-bl" />
      <span className="wnt-nail wnt-nail-br" />
      {/* WANTED headline */}
      <div className="wnt-head">
        <div className="wnt-rule" />
        <div className="poster wnt-headline">WANTED</div>
        <div className="mono wnt-sub">DEAD · OR · ALIVE</div>
        <div className="wnt-rule" />
      </div>
      {/* art panel — torn vignette */}
      <div className="wnt-photo" style={{ ['--accent' as any]: a } as CSSProperties}>
        <div className="wnt-photo-inner">
          <RoleArt id={role.id} accent={a} sub={role.sub} />
        </div>
      </div>
      {/* name slug */}
      <div className="wnt-name display">{role.title[t]}</div>
      <div className="mono wnt-latin">«{role.latin}»</div>
      {/* reward strip */}
      <div className="wnt-reward mono">
        <span>REWARD</span>
        <span className="wnt-amount">${reward}</span>
        <span>№{role.number}</span>
      </div>
    </div>
  );
}

// Per-role bounty in dollars (descending threat). The Don is the head of the
// family, so his price is the highest; muscle and assassins follow; vigilantes
// who walk into mafia territory are also worth real money. Roles without an
// explicit entry get a hash-based amount so unfamiliar roles still feel
// distinct. The list is intentionally tuned, not random.
const REWARD_BY_ID: Record<string, string> = {
  don:         '100,000',
  hitman:       '75,000',
  capo:         '50,000',
  consigliere:  '50,000',
  enforcer:     '40,000',
  goon:         '25,000',
  saboteur:     '25,000',
  strangler:    '25,000',
  framer:       '15,000',
  mafia:        '15,000',
  janitor:      '10,000',
  wheelman:     '10,000',
  mafia_lover:   '7,500',
  // Town vigilantes who hunt the mafia themselves — bounty placed BY the
  // mafia for show.
  vigilante:    '20,000',
};

function rewardFor(id: string): string {
  const explicit = REWARD_BY_ID[id];
  if (explicit) return explicit;
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  const tiers = ['500', '1,000', '2,500', '5,000'];
  return tiers[Math.abs(h) % tiers.length];
}

// ──────────────────────────────────────────────────────────────────────────
// TAROT — symmetric arcana card with mirrored bottom and ornamental frame.
// ──────────────────────────────────────────────────────────────────────────
function TarotFront({ role, t }: FrontProps) {
  const a = role.accent;
  const roman = romanize(role.number);
  return (
    <div className="trt" style={{ ['--accent' as any]: a } as CSSProperties}>
      <div className="trt-paper" />
      <div className="trt-stars" />
      {/* outer + inner gold border */}
      <div className="trt-frame trt-frame-outer" />
      <div className="trt-frame trt-frame-inner" />
      {/* corner ornaments */}
      <TarotCorner pos="tl" accent={a} />
      <TarotCorner pos="tr" accent={a} />
      <TarotCorner pos="bl" accent={a} />
      <TarotCorner pos="br" accent={a} />
      {/* faint roman numeral behind everything */}
      <div className="trt-roman" style={{ color: a }}>{roman}</div>
      {/* top title arched */}
      <div className="trt-title-top display" style={{ color: a }}>{role.title[t]}</div>
      {/* central art */}
      <div className="trt-art">
        <RoleArt id={role.id} accent={a} sub={role.sub} />
      </div>
      {/* mirror — same art, rotated, dimmed */}
      <div className="trt-art trt-art-mirror">
        <RoleArt id={role.id} accent={a} sub={role.sub} />
      </div>
      {/* divider with center pip */}
      <div className="trt-divider">
        <span className="trt-pip" style={{ background: a }} />
      </div>
      {/* mirrored bottom title */}
      <div className="trt-title-bot display" style={{ color: a }}>{role.title[t]}</div>
      {/* roman numeral chip */}
      <div className="mono trt-numeral" style={{ color: a, borderColor: a }}>{roman}</div>
    </div>
  );
}

function TarotCorner({ pos, accent }: { pos: 'tl' | 'tr' | 'bl' | 'br'; accent: string }) {
  return (
    <svg className={`trt-corner trt-corner-${pos}`} viewBox="0 0 40 40" aria-hidden>
      <path
        d="M2 2 L18 2 M2 2 L2 18 M2 18 Q10 10 18 2 M8 22 Q14 14 22 8"
        fill="none" stroke={accent} strokeWidth="1.6" strokeLinecap="round"
      />
      <circle cx="20" cy="20" r="2.4" fill={accent} />
    </svg>
  );
}

function romanize(n: string): string {
  const num = parseInt(n, 10);
  if (Number.isNaN(num) || num <= 0) return n.toUpperCase();
  const map: [number, string][] = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ];
  let v = num;
  let out = '';
  for (const [val, sym] of map) while (v >= val) { out += sym; v -= val; }
  return out;
}

// ──────────────────────────────────────────────────────────────────────────
// PARLOR — playing card with corner pips (rotated mirror in opposite corner).
// ──────────────────────────────────────────────────────────────────────────
function ParlorFront({ role, t }: FrontProps) {
  const a = role.accent;
  const pip = pipFor(role.id);
  return (
    <div className="prl">
      <div className="prl-paper" />
      <div className="prl-pattern" />
      {/* top-left pip */}
      <div className="prl-corner prl-corner-tl" style={{ color: a }}>
        <div className="display prl-pip-num">{pipNumber(role.number)}</div>
        <div className="prl-pip">{pip}</div>
      </div>
      {/* bottom-right pip — rotated 180 */}
      <div className="prl-corner prl-corner-br" style={{ color: a }}>
        <div className="display prl-pip-num">{pipNumber(role.number)}</div>
        <div className="prl-pip">{pip}</div>
      </div>
      {/* central oval with art */}
      <div className="prl-vignette" style={{ borderColor: a }}>
        <div className="prl-vignette-art">
          <RoleArt id={role.id} accent={a} sub={role.sub} />
        </div>
      </div>
      {/* center title strip */}
      <div className="prl-name">
        <span className="display" style={{ color: a }}>{role.title[t]}</span>
        <span className="mono prl-latin">{role.latin}</span>
      </div>
    </div>
  );
}

function pipFor(id: string): ReactNode {
  // Four "suits" derived from a stable hash of the role id.
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  const suits = ['♠', '♥', '♦', '♣'];
  return suits[Math.abs(h) % suits.length];
}

function pipNumber(n: string): string {
  // Strip leading zero for prettier corner display.
  return n.replace(/^0+/, '') || n;
}

// ──────────────────────────────────────────────────────────────────────────
// VELLUM — refined cream letterpress (the "default" town look done well).
// Grid layout instead of absolute positioning so things actually align.
// ──────────────────────────────────────────────────────────────────────────
function VellumFront({ role, t, factionLabel }: FrontProps) {
  const a = role.accent;
  return (
    <div className="vlm">
      <div className="vlm-paper" />
      <div className="vlm-engrave" />
      <div className="vlm-frame" style={{ borderColor: '#1a1a22' }} />
      {/* left margin rail with rotated number */}
      <div className="vlm-rail">
        <span className="mono vlm-rail-num">№{role.number}</span>
        <span className="vlm-rail-dots" />
        <span className="mono vlm-rail-faction">{factionLabel}</span>
      </div>
      {/* main grid */}
      <div className="vlm-stack">
        <div className="vlm-head sub">
          <span>{factionLabel}</span>
          <span className="mono vlm-head-num">№{role.number}</span>
        </div>
        {/* Cream slab framed by the role accent. The icon is rendered in ink
            on cream so it's always legible — when the slab itself was tinted
            with role.accent (the previous design) icons drawn in role.accent
            became the same colour as the background and disappeared. */}
        <div className="vlm-slab" style={{ borderColor: a }}>
          <RoleArt id={role.id} accent="#0a0a0c" sub={role.sub} />
        </div>
        <div className="vlm-title">
          <div className="display vlm-title-text">{role.title[t]}</div>
          <div className="mono vlm-latin">{role.latin}</div>
        </div>
      </div>
      {/* wax seal in bottom-right */}
      <div className="vlm-seal" style={{ background: a, color: '#1a1a22' }}>
        <span className="display vlm-seal-monogram">{role.title[t][0].toUpperCase()}</span>
      </div>
      {/* footer serial */}
      <div className="vlm-foot mono" style={{ color: '#5b4f38' }}>
        <span>{role.number} / {totalCount(role.faction)}</span>
        <span>· MAFIA NOIR ·</span>
        <span>{role.latin.slice(0, 3)}</span>
      </div>
    </div>
  );
}

function totalCount(faction: string): string {
  return faction === 'town' ? '18' : faction === 'mafia' ? '12' : '09';
}

// ──────────────────────────────────────────────────────────────────────────
// INFERNAL — scorched paper with ember glow, jagged edges, ash.
// ──────────────────────────────────────────────────────────────────────────
function InfernalFront({ role, t, factionLabel }: FrontProps) {
  const a = role.accent;
  return (
    <div className="inf" style={{ ['--accent' as any]: a } as CSSProperties}>
      <div className="inf-paper" />
      {/* scorched edges via SVG mask shape */}
      <svg className="inf-burn" viewBox="0 0 240 360" preserveAspectRatio="none" aria-hidden>
        <defs>
          <radialGradient id={`infg-${role.id}`} cx="50%" cy="50%" r="60%">
            <stop offset="40%" stopColor="rgba(255,90,30,0)" />
            <stop offset="80%" stopColor="rgba(255,90,30,0.18)" />
            <stop offset="100%" stopColor="rgba(255,30,10,0.55)" />
          </radialGradient>
        </defs>
        <rect width="240" height="360" fill={`url(#infg-${role.id})`} />
      </svg>
      <div className="inf-crack" />
      <div className="inf-grain" />
      {/* irregular charred frame */}
      <svg className="inf-frame" viewBox="0 0 240 360" preserveAspectRatio="none" aria-hidden>
        <path
          d="M14 10 Q30 6 60 8 Q90 4 120 10 Q150 4 180 8 Q210 6 226 10 Q230 40 224 80 Q232 120 228 160 Q234 200 226 240 Q232 280 226 320 Q228 350 210 352 Q180 356 150 350 Q120 356 90 350 Q60 354 30 350 Q14 352 12 320 Q6 280 10 240 Q4 200 10 160 Q4 120 10 80 Q6 40 14 10 Z"
          fill="none" stroke={a} strokeWidth="1.4" strokeDasharray="4 6" strokeLinecap="round" opacity="0.7"
        />
      </svg>
      {/* ember dots */}
      {Array.from({ length: 14 }).map((_, i) => (
        <span key={i} className="inf-ember" style={{ background: a, ['--i' as any]: i } as CSSProperties} />
      ))}
      {/* head */}
      <div className="inf-head">
        <span className="mono inf-tag" style={{ color: a, borderColor: a }}>{factionLabel}</span>
        <span className="mono inf-num">№{role.number}</span>
      </div>
      {/* title — stenciled with glow */}
      <div className="inf-title display" style={{ color: a, textShadow: `0 0 12px ${a}, 0 0 24px ${a}88` }}>
        {role.title[t]}
      </div>
      <div className="inf-latin mono" style={{ color: a }}>{role.latin}</div>
      {/* art with halo */}
      <div className="inf-art">
        <div className="inf-halo" style={{ background: `radial-gradient(circle, ${a}66, transparent 70%)` }} />
        <RoleArt id={role.id} accent={a} sub={role.sub} />
      </div>
      {/* bottom: "DANGER" stamp */}
      <div className="inf-stamp mono" style={{ color: a, borderColor: a }}>
        <span>{t === 'ru' ? '⚠ ОПАСНО' : '⚠ НЕБЕЗПЕЧНО'}</span>
        <span className="inf-stamp-sep">·</span>
        <span>{role.latin}</span>
      </div>
    </div>
  );
}
